import { CSSProperties, useCallback, useMemo, useRef } from "react";
import { Cell, Row } from "react-aria-components";

import TableContextProvider from "components/Table/Context";
import Table from "components/Table";
import TableRow from "components/Table/Row";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored, InfoCircle } from "components/icons/generated";
import PageInfo from "components/PageWrapper/Info";
import Box from "ds/components/Box";
import PageWrapperTitle from "components/PageWrapper/Title";
import ButtonIcon from "ds/components/ButtonIcon";
import SearchInput from "components/SearchInput";
import LinkButton from "ds/components/Button/LinkButton";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";

import PermissionsListItem from "./ListItem";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  PERMISSIONS_COLUMN_CONFIG,
  PERMISSIONS_COLUMNS_STORAGE_KEY,
  PERMISSIONS_CUSTOM_END_COLUMN_CONFIG,
  ROW_HEADER_ID,
  SORT_CONFIG,
} from "./constants";
import { ANALYTICS, PERMISSIONS } from "../constants";
import { Permission, RoleView } from "../types";
import { useFilteredEntities } from "./useFilteredEntities";
import { showRoleInfoDrawer } from "../InfoDrawer";
import useRoleAnalytics from "../useRoleAnalytics";

type RolePermissionsProps = {
  role: RoleView;
};

const RolePermissions = ({ role }: RolePermissionsProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const renderRow = useCallback(
    (
      item: Partial<Permission> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return (
          <Row>
            <Cell style={{ height: item.height }} />
          </Row>
        );
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <PermissionsListItem permission={item as Permission} />
        </TableRow>
      );
    },
    []
  );

  const trackSegmentAnalyticsEvent = useRoleAnalytics();

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showRoleInfoDrawer();
  };

  const permissions = useMemo(
    () =>
      role.actions.map((action) => ({
        ...PERMISSIONS[action],
        id: action,
        deleteDisabledMessage:
          role.actions.length === 1 ? "Roles must include at least one permission." : undefined,
        parentRole: role,
      })) || [],
    [role]
  );

  const filteredPermissions = useFilteredEntities(permissions, sortDescriptor);

  return (
    <>
      <PageInfo
        title={
          <Box direction="row" align="center" gap="medium">
            <PageWrapperTitle>Permissions</PageWrapperTitle>
            <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
              Open info drawer
            </ButtonIcon>
          </Box>
        }
      >
        <SearchInput placeholder="Search..." filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY} />
        <LinkButton
          analyticsPage={AnalyticsPageOrganization.OrganizationRole}
          analyticsTitle="Add Role Permissions Clicked"
          analyticsProps={{
            roleName: role.name,
          }}
          variant="primary"
          to={`/settings/role/${role.id}/permissions`}
        >
          Add permissions
        </LinkButton>
      </PageInfo>
      <TableContextProvider
        columnsConfig={PERMISSIONS_COLUMN_CONFIG}
        endCustomColumns={PERMISSIONS_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={PERMISSIONS_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        {!!filteredPermissions?.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table
              ariaLabel="Permissions"
              items={filteredPermissions}
              ref={virtualizedListContainerRef}
            >
              {renderRow}
            </Table>
          </TableWrapper>
        )}
        {!filteredPermissions.length && !!permissions?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableContextProvider>
    </>
  );
};

export default RolePermissions;
